import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Save, Building2, Upload, X, Image } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useConfirm } from '../context/ConfirmContext';

export const SettingsPage = () => {
  const confirm = useConfirm();
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    address_street: '',
    address_zip: '',
    address_city: '',
    address_canton: '',
    address_country: 'CH',
    phone: '',
    email: '',
    website: '',
    uid_che: '',
    bank_name: '',
    bank_iban: '',
    bank_swift: '',
    logo_url: '',
    footer_text: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.getCompanySettings();
      setCompanySettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanySettings = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.updateCompanySettings(companySettings);
      toast.success('Impostazioni aziendali salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo file non supportato. Usa PNG, JPG, WEBP o SVG.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Max 5MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const response = await api.uploadCompanyLogo(file);
      setCompanySettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo caricato con successo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    const ok = await confirm({
      title: 'Elimina logo',
      description: 'Il logo aziendale sarà rimosso dal sistema e da tutti i nuovi documenti.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;

    try {
      await api.deleteCompanyLogo();
      setCompanySettings(prev => ({ ...prev, logo_url: '' }));
      toast.success('Logo eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione del logo');
    }
  };

  const getLogoFullUrl = (logoUrl) => {
    if (!logoUrl) return null;
    if (logoUrl.startsWith('http')) return logoUrl;
    return `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`;
  };

  if (loading) {
    return <div className="p-8">Caricamento...</div>;
  }

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Impostazioni</h1>
        <p className="text-slate-600">Configura l'applicazione e personalizza per la tua azienda</p>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList>
          <TabsTrigger value="company">Informazioni Aziendali</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-primary/10 text-primary">
                  <Building2 size={24} />
                </div>
                <div>
                  <CardTitle>Dati Aziendali</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">
                    Questi dati verranno utilizzati nelle fatture e nei documenti ufficiali
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveCompanySettings} className="space-y-6">
                {/* Tenant ID - Read Only */}
                {companySettings.tenant_id && (
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-slate-600 text-sm">ID Tenant (identificativo azienda)</Label>
                        <p className="font-mono text-sm text-slate-900 mt-1" data-testid="tenant-id-display">
                          {companySettings.tenant_id}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(companySettings.tenant_id);
                          toast.success('ID copiato');
                        }}
                      >
                        Copia
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nome Azienda *</Label>
                    <Input
                      id="company_name"
                      value={companySettings.company_name}
                      onChange={(e) => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="uid_che">UID/CHE</Label>
                    <Input
                      id="uid_che"
                      value={companySettings.uid_che}
                      onChange={(e) => setCompanySettings({ ...companySettings, uid_che: e.target.value })}
                      placeholder="CHE-123.456.789"
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">Indirizzo</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="address_street">Via *</Label>
                      <Input
                        id="address_street"
                        value={companySettings.address_street}
                        onChange={(e) => setCompanySettings({ ...companySettings, address_street: e.target.value })}
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="address_zip">NAP *</Label>
                        <Input
                          id="address_zip"
                          value={companySettings.address_zip}
                          onChange={(e) => setCompanySettings({ ...companySettings, address_zip: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_city">Città *</Label>
                        <Input
                          id="address_city"
                          value={companySettings.address_city}
                          onChange={(e) => setCompanySettings({ ...companySettings, address_city: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_canton">Cantone *</Label>
                        <Input
                          id="address_canton"
                          value={companySettings.address_canton}
                          onChange={(e) => setCompanySettings({ ...companySettings, address_canton: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">Contatti</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefono</Label>
                      <Input
                        id="phone"
                        value={companySettings.phone}
                        onChange={(e) => setCompanySettings({ ...companySettings, phone: e.target.value })}
                        placeholder="+41 91 123 45 67"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={companySettings.email}
                        onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Sito Web</Label>
                      <Input
                        id="website"
                        value={companySettings.website}
                        onChange={(e) => setCompanySettings({ ...companySettings, website: e.target.value })}
                        placeholder="www.esempio.ch"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">Coordinate Bancarie</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="bank_name">Nome Banca</Label>
                      <Input
                        id="bank_name"
                        value={companySettings.bank_name}
                        onChange={(e) => setCompanySettings({ ...companySettings, bank_name: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bank_iban">IBAN</Label>
                        <Input
                          id="bank_iban"
                          value={companySettings.bank_iban}
                          onChange={(e) => setCompanySettings({ ...companySettings, bank_iban: e.target.value })}
                          placeholder="CH93 0076 2011 6238 5295 7"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank_swift">SWIFT/BIC</Label>
                        <Input
                          id="bank_swift"
                          value={companySettings.bank_swift}
                          onChange={(e) => setCompanySettings({ ...companySettings, bank_swift: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">Personalizzazione Documenti</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Logo Aziendale</Label>
                      <div className="flex items-start gap-4">
                        {/* Logo Preview */}
                        <div className="w-32 h-32 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden">
                          {companySettings.logo_url ? (
                            <img 
                              src={getLogoFullUrl(companySettings.logo_url)} 
                              alt="Logo aziendale" 
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <Image size={32} className="text-slate-300" />
                          )}
                        </div>
                        
                        {/* Upload Controls */}
                        <div className="flex-1 space-y-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleLogoUpload}
                            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                            className="hidden"
                            id="logo-upload"
                          />
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingLogo}
                            >
                              <Upload size={16} className="mr-2" />
                              {uploadingLogo ? 'Caricamento...' : 'Carica Logo'}
                            </Button>
                            {companySettings.logo_url && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleDeleteLogo}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <X size={16} className="mr-2" />
                                Rimuovi
                              </Button>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            Formati supportati: PNG, JPG, WEBP, SVG. Max 5MB.
                          </p>
                          <p className="text-xs text-slate-500">
                            Il logo verrà visualizzato nelle fatture PDF.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="footer_text">Testo Footer Fatture</Label>
                      <Textarea
                        id="footer_text"
                        value={companySettings.footer_text}
                        onChange={(e) => setCompanySettings({ ...companySettings, footer_text: e.target.value })}
                        rows={3}
                        placeholder="Es: Grazie per la vostra fiducia..."
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={saving} data-testid="save-settings-btn">
                    <Save size={18} className="mr-2" />
                    {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
